<template>
  <vx-card>
    <form-wizard
      color="rgba(var(--vs-primary), 1)"
      errorColor="rgba(var(--vs-danger), 1)"
      :title="null"
      :subtitle="null"
      :finishButtonText="$t('forms.submit')"
      :nextButtonText="$t('forms.next')"
      :backButtonText="$t('forms.back')"
      @on-complete="requestComplete"
      @on-change="mapLoader"
    >
      <!-- tab content -->
      <tab-content
        :key="stepName"
        v-for="(step, stepName) in stepDefs"
        :title="step.title"
        class="mb-5"
        :icon="step.icon ? `${step.iconPack ? '' : 'feather'} ${step.icon}` : null"
        :before-change="() => validateStep(stepName)"
      >
        <form :data-vv-scope="stepName" ref="stepName">
          <!--Header Slot-->
          <slot :name="`${stepName}_header`" />
          <vs-row>
            <div
              :class="'vx-col ' + field.styleClasses"
              v-for="(field, fieldName) in step.fields"
              :key="fieldName"
            >
              <div class="w-full mt-3 md:pl-1 mb-0 inline-block">
                <!--Label and Icon-->
                <label
                  v-if="!['alert', 'button'].includes(field.type)"
                  :class="errors.has(`${stepName}.${fieldName}`) ? 'text-danger' : ''"
                >
                  <vx-tooltip v-if="field.tooltip" :text="field.tooltip" style="display: inherit">
                    <i :class="`${field.iconPack} feather-icon ${field.icon}`"></i>
                    {{ field.label }}
                    <i class="feather feather-icon icon-info"></i>
                  </vx-tooltip>
                  <template v-else>
                    <i :class="`${field.iconPack} feather-icon ${field.icon}`"></i>
                    {{ field.label }}
                  </template>
                </label>

                <!--INPUT TYPE-->
                <vs-input
                  v-if="field.type === 'input'"
                  :name="fieldName"
                  :type="field.inputType"
                  v-model="requestData[fieldName]"
                  v-validate="field.validator"
                  :data-vv-as="field.label"
                  class="w-full rf-input"
                />

                <!--DatePicker -->
                <date-picker
                  v-if="field.type === 'datetime'"
                  :type="field.inputType"
                  v-model="requestData[fieldName]"
                  :min="field.min"
                  :max="field.max"
                  :initial-value="field.initValue"
                  :element="`${stepName}.${fieldName}`"
                />
                <vs-input
                  v-if="field.type === 'datetime'"
                  :name="fieldName"
                  :value="
                    requestData[fieldName]
                      ? $moment(requestData[fieldName])
                          .locale($vs.rtl ? 'fa' : 'en')
                          .format(field.format ? field.format : 'HH:mm dddd, DD MMMM YYYY')
                      : ''
                  "
                  :id="`${stepName}.${fieldName}`"
                  v-validate="field.validator"
                  :data-vv-as="field.label"
                  class="w-full rf-input"
                />

                <!-- Map -->
                <open-street-map
                  v-if="field.type === 'map'"
                  v-model="requestData[fieldName]"
                  class="w-full"
                  :height="field.height"
                  :center="field.center"
                  :zoom="field.zoom"
                  :view-only="field.viewOnly"
                  :disable-fit="field.disableFit"
                  :geojson="field.geojson"
                  :markers="field.markers"
                  :polygons="field.polygons"
                  :polygon-opacity="field.polygonOpacity"
                  :validate-selection="field.validateSelection"
                />
                <div v-if="field.type === 'map' && !field.hideAddress" class="vx-row w-full mt-5">
                  <p class="vx-col">
                    <address-string :address="requestData[fieldName]" />
                  </p>
                </div>
                <vs-input
                  v-if="field.type === 'map'"
                  type="hidden"
                  :name="fieldName"
                  v-model="requestData[fieldName].province"
                  v-validate="field.validator"
                  :data-vv-as="field.label"
                />

                <!--Select Options -->
                <v-select
                  v-if="field.type === 'select'"
                  :name="fieldName"
                  v-model="requestData[fieldName]"
                  :placeholder="field.placeholder"
                  v-validate="field.validator"
                  :data-vv-as="field.label"
                  :multiple="field.multiple"
                  :taggable="field.taggable"
                  :clearable="field.clearable"
                  :options="field.options"
                  :reduce="option => option.value"
                  :dir="$vs.rtl ? 'rtl' : 'ltr'"
                  class="w-full mb-0"
                >
                </v-select>

                <!--Number Input -->
                <vs-input-number
                  v-if="field.type === 'number'"
                  :name="fieldName"
                  v-model="requestData[fieldName]"
                  v-validate="field.validator"
                  :data-vv-as="field.label"
                  :min="field.min"
                  :max="field.max"
                  class="w-full mb-2 ml-auto mr-auto"
                  style="width: 100px"
                />

                <!--Textarea Input -->
                <vs-textarea
                  v-if="field.type === 'textarea'"
                  :name="fieldName"
                  v-model="requestData[fieldName]"
                  v-validate="field.validator"
                  :data-vv-as="field.label"
                  :width="field.width"
                  :height="field.height + 'px'"
                  :placeholder="field.placeholder"
                  class="w-full mb-0 textarea-height"
                />

                <!--Radio-->
                <ul v-if="field.type === 'radio'" class="w-full mb-2 radio-ul">
                  <li v-for="item in field.options" :key="item.value">
                    <vs-radio
                      :name="fieldName"
                      v-model="requestData[fieldName]"
                      v-validate="field.validator"
                      :data-vv-as="field.label"
                      :vs-value="item.value"
                    >
                      {{ item.label }}
                    </vs-radio>
                  </li>
                </ul>
              </div>

              <!--Subject Service Select-->
              <select-subject-services
                v-if="field.type === 'select_services'"
                :subject="field.subject"
                v-model="requestData[fieldName]"
              />
              <vs-input
                v-if="field.type === 'select_services'"
                type="hidden"
                :name="fieldName"
                v-model="requestData[fieldName].length"
                v-validate="
                  field.validator === 'required' ? field.validator + '|regex:^[1-9][0-9]*$' : field.validator
                "
                :data-vv-as="field.label"
                :data-vv-message="field.label"
              />

              <!--Address Select-->
              <select-address v-if="field.type === 'select_address'" v-model="requestData[fieldName]" />
              <vs-input
                v-if="field.type === 'select_address'"
                type="hidden"
                :name="fieldName"
                v-model="requestData[fieldName]"
                v-validate="
                  field.validator === 'required' ? field.validator + '|regex:^[0-9]*$' : field.validator
                "
                :data-vv-as="field.label"
                :data-vv-message="field.label"
              />

              <upload-file
                v-if="field.type === 'upload_file'"
                v-model="requestData[fieldName]"
                :multiple="field.multiple"
                :title="field.title"
                :upload-auto="field.uploadAuto"
                :directory="field.directory"
                :drop="field.drop"
                :drop-directory="field.dropDirectory"
              />
              <vs-input
                v-if="field.type === 'upload_file'"
                type="hidden"
                :name="fieldName"
                v-model="requestData[fieldName].length"
                v-validate="
                  field.validator === 'required' ? field.validator + '|regex:^[1-9][0-9]*$' : field.validator
                "
                :data-vv-as="field.label"
                data-vv-message="الزامی است"
              />

              <!--Switch -->
              <vs-switch
                v-if="field.type === 'switch'"
                :name="fieldName"
                v-model="requestData[fieldName]"
                v-validate="field.validator"
                :data-vv-as="field.label"
                class="m-auto mt-3"
              />

              <!--Button -->
              <vs-button
                v-if="field.type === 'button'"
                :color="field.color"
                :type="field.btnType"
                :icon-pack="field.iconPack"
                :icon="field.icon"
                :target="field.target"
                :href="field.href"
              >
                {{ field.label }}
              </vs-button>

              <!--Alert -->
              <vs-alert
                v-if="field.type === 'alert'"
                :color="field.color"
                :title="field.title"
                :icon-pack="field.iconPack"
                :icon="field.icon"
                active="true"
              >
                {{ field.text }}
              </vs-alert>
              <!--Validation Error -->
              <span class="text-danger text-sm md:pl-3" v-if="errors.has(`${stepName}.${fieldName}`)">{{
                errors.first(`${stepName}.${fieldName}`)
              }}</span>
              <span class="vx-row m-1 text-light text-sm md:pl-3" v-if="field.hint">{{ field.hint }}</span>
            </div>
          </vs-row>

          <!--Footer Slot-->
          <slot :name="`${stepName}_footer`" />
        </form>
      </tab-content>
    </form-wizard>
  </vx-card>
</template>

<script>
import Vue from 'vue'
import { FormWizard, TabContent } from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import OpenStreetMap from '@/views/components/maps/OpenStreetMap.vue'
import AddressString from '@/views/components/maps/AddressString'
import SelectSubjectServices from '@/views/components/forms/SelectSubjectServices'
import SelectAddress from '@/views/components/forms/SelectAddress'
import UploadFile from '@/views/components/forms/UploadFile'

export default {
  props: {
    stepDefs: {
      type: Object,
      required: true,
      validator(obj) {
        const types = [
          'input',
          'datetime',
          'map',
          'select',
          'radio',
          'number',
          'textarea',
          'alert',
          'button',
          'switch',
          'select_services',
          'select_address',
          'upload_file'
        ]
        const datetimeInputTypes = ['time', 'datetime', 'date', 'month', 'year']

        const varDecReg = new RegExp('^(?=_[a-zA-Z_0-9]|[a-zA-Z])[a-zA-Z_0-9]+$') // for check if name is variable declaration and not started with numbers etc..

        /* eslint-disable no-console */
        for (const stepName in obj) {
          if (!varDecReg.test(stepName))
            console.error(`[Request Form Warning]: Key "${stepName}" is not variable declaration format`)

          const fields = obj[stepName].fields
          for (const fieldName in fields) {
            if (!varDecReg.test(fieldName))
              console.error(
                `[Request Form Warning]: Key "${stepName}.${fieldName}" is not variable declaration format`
              )

            const f = fields[fieldName]

            // default attributes:  name  label  validator
            if (f.type === 'input') {
              // attributes:  inputType  icon  iconPack

              if (!f.label)
                console.error(`[Request Form Warning]: "${stepName}.${fieldName}" field has not label value`)
            } else if (f.type === 'datetime') {
              // attributes: inputType  min   max   icon  iconPack

              if (!datetimeInputTypes.includes(f.inputType)) {
                console.error(
                  `[Request Form Error]: inputType in "${stepName}.${fieldName}" is not correct format`
                )
              }
            }
            if (!types.includes(f.type)) {
              throw new TypeError(
                `[Request Form Error]: type in "${stepName}.${fieldName}" is not correct format`
              )
            }
          }
        }
        /* eslint-enable no-console */
        return true
      }
    }
  },
  data() {
    return {
      requestData: {}
    }
  },
  watch: {
    requestData(value) {
      this.$emit('input', value)
    }
  },
  methods: {
    mapLoader() {
      // Fix Tile Loading Bug
      window.dispatchEvent(new Event('resize'))
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    validateStep(step) {
      return new Promise((resolve, reject) => {
        this.$validator.validateAll(step).then(result => {
          if (result) {
            resolve(true)
          } else {
            reject('correct all values')
          }
        })
      })
    },
    requestComplete() {
      this.$emit('onComplete', this.requestData)
    }
  },
  created() {
    // Set initial value for inputs with "initValue" key
    for (const stepName in this.stepDefs) {
      const fields = this.stepDefs[stepName].fields
      for (const fieldName in fields) {
        const f = fields[fieldName]
        if (f.initValue) {
          // fix init bug
          Vue.set(this.requestData, fieldName, f.initValue)
        }
        if (f.type === 'map') {
          Vue.set(this.requestData, fieldName, {})
        }
        if (f.type === 'select_services') {
          Vue.set(this.requestData, fieldName, [])
        }
        if (f.type === 'upload_file') {
          Vue.set(this.requestData, fieldName, [])
        }
      }
    }
  },
  components: {
    SelectAddress,
    FormWizard,
    TabContent,
    OpenStreetMap,
    AddressString,
    SelectSubjectServices,
    UploadFile
  }
}
</script>
<style lang="scss">
.rf-input {
  span {
    display: none;
  }
}
.radio-ul {
  list-style-type: none;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  li {
    flex: 0 0 50%;
    padding: 1em;
    display: flex;
    align-items: flex-end;
  }
}
.textarea-height {
  overflow: hidden;
  textarea {
    height: inherit;
  }
}
</style>
